import { EstimateData } from '../types/estimation';
import { DetailedEstimationData } from '../types/estimation-items';

export type EstimationFormatVersion = 'legacy' | 'current';

export interface VersionDetectionResult {
  version: EstimationFormatVersion;
  needsMigration: boolean;
  hasLegacyPriceRange: boolean;
  hasTVAData: boolean;
}

export class EstimationVersionDetector {
  private static instance: EstimationVersionDetector;

  private constructor() {}

  public static getInstance(): EstimationVersionDetector {
    if (!EstimationVersionDetector.instance) {
      EstimationVersionDetector.instance = new EstimationVersionDetector();
    }
    return EstimationVersionDetector.instance;
  }

  public detectVersion(data: any): VersionDetectionResult {
    const hasLegacyPriceRange = this.hasLegacyPriceRangeStructure(data);
    const hasTVAData = this.hasTVAInformation(data);
    const hasBuildingAge = this.hasBuildingAge(data);

    const isLegacy = hasLegacyPriceRange || (!hasTVAData && !hasBuildingAge);

    return {
      version: isLegacy ? 'legacy' : 'current',
      needsMigration: isLegacy,
      hasLegacyPriceRange,
      hasTVAData
    };
  }

  private hasLegacyPriceRangeStructure(data: any): boolean {
    if (!data.priceRange) return false;

    return Array.isArray(data.priceRange.sections) &&
           data.priceRange.sections.length > 0 &&
           data.priceRange.sections[0].items !== undefined;
  }

  private hasTVAInformation(data: any): boolean {
    const categories = [
      'preparation', 'demolition', 'gros-oeuvre', 'vrd',
      'isolation', 'cloisons', 'technique', 'sanitaire',
      'revetements', 'menuiserie', 'finitions', 'exterieurs'
    ];

    for (const category of categories) {
      if (data[category]) {
        const categoryData = data[category];

        for (const key in categoryData) {
          const item = categoryData[key];
          if (typeof item === 'object' && item !== null) {
            for (const subKey in item) {
              const subItem = item[subKey];
              if (subItem && typeof subItem === 'object' && subItem.tvaRate !== undefined) {
                return true;
              }
            }
          }
        }
      }
    }

    return false;
  }

  private hasBuildingAge(data: any): boolean {
    return data.description?.buildingAge !== undefined &&
           data.description?.buildingAge !== null;
  }

  public isLegacyFormat(data: any): boolean {
    if (this.isQuickEstimation(data)) {
      return false;
    }
    return this.detectVersion(data).version === 'legacy';
  }

  private isQuickEstimation(data: any): boolean {
    const hasQuickFields =
      data.propertyType !== undefined &&
      data.area !== undefined &&
      data.renovationLevel !== undefined;

    const detailedCategories = [
      'preparation', 'demolition', 'gros-oeuvre', 'vrd',
      'isolation', 'cloisons', 'technique', 'sanitaire',
      'revetements', 'menuiserie', 'finitions', 'exterieurs'
    ];

    const hasDetailedCategories = detailedCategories.some(
      category => data[category] !== undefined
    );

    return hasQuickFields && !hasDetailedCategories;
  }

  public isLegacyDetailedEstimate(data: any): boolean {
    if (this.isQuickEstimation(data)) {
      return false;
    }
    return this.isLegacyFormat(data);
  }

  public needsMigration(data: any): boolean {
    return this.detectVersion(data).needsMigration;
  }
}

export const versionDetector = EstimationVersionDetector.getInstance();
